import { FetchMethod, useCodeerApi } from "../useCodeerApi"
import {
  AgentSchema,
  AgentLLMModelSettingsSchema,
  HumanHandoffConfigSchema,
  transToAgentLLMModelSettingsMap,
  transToAiTakeoverGreeting,
  transToHumanHandoffConfig,
  transToOutputPresentationPolicy,
  transToUnifiedTool,
  unifiedToolToSchema,
} from "./types"
import type { OutputPresentationPolicySchema as AgentResponseOutputPresentationPolicySchema } from "./types"
import { RequestFormSchema } from "@/types/requestForm"
import {
  Response,
  AgentHistory,
  ApiAttachment,
  AgentLLMModelSettings,
  AgentLLMModelSettingsMap,
  AgentResponseMode,
  clampOutputPresentationMaxItems,
  DEFAULT_AGENT_RESPONSE_MODE,
  DEFAULT_HUMAN_HANDOFF_CONFIG,
  normalizeAiTakeoverGreeting,
  HumanHandoffConfig,
  OutputPresentationKind,
  OutputPresentationPolicy,
  UnifiedTool,
  UnifiedToolType,
} from "@/types"
import type {
  Text2SpeechLanguageOption,
  Text2SpeechModelOption,
  Text2SpeechVoiceLanguageOption,
  Text2SpeechVoiceOption,
  Text2SpeechProvider,
} from "@/types/text2speechTool"
import type { DraftPolicy } from "@/types"
import type {
  HttpRequestAuthLocation,
  HttpRequestInputType,
  HttpRequestAuthType,
  HttpRequestMethod,
} from "@/types/httpRequestTool"
import { UploadingFile } from "@/components/Conversation/FileUploadTypes"
import { FileAttachmentScope } from "@/types"

// Helper function to convert API attachments to UploadingFile objects
export const apiAttachmentsToUploadingFiles = (
  attachments: ApiAttachment[]
): UploadingFile[] => {
  return attachments.map((attachment) => {
    // Create a mock File object for already uploaded files
    // Use original_name as the filename, fallback to a generic name if not available
    const fileName = attachment.original_name || `attachment-${attachment.id}`
    const mockFile = new File([], fileName, {
      type: attachment.content_type || "application/octet-stream",
    })

    // Add the size property to the mock file
    Object.defineProperty(mockFile, "size", {
      value: attachment.size || 0,
      writable: false,
    })

    const uploadingFile: UploadingFile = {
      file: mockFile,
      status: "uploaded" as const,
      uuid: attachment.id,
      scope:
        attachment.scope === "persistent"
          ? FileAttachmentScope.Persistent
          : FileAttachmentScope.Ephemeral,
      url: attachment.source_url || undefined,
    }

    return uploadingFile
  })
}

export interface CreateAgentRequest {
  name: string
  description: string
  workspace_id: string
  system_prompt: string
  unified_tools: UnifiedToolSchema[]
  attachment_ids: string[]
  use_search: boolean
  llm_model?: string | null
  llm_model_settings?: Record<string, AgentLLMModelSettingsSchema>
  suggested_questions?: string[]
  response_mode: AgentResponseMode
  human_handoff: HumanHandoffConfigSchema
  ai_takeover_greeting: string
  output_presentation_policy?: OutputPresentationPolicySchema
  pending_knowledge_bindings?: Array<{
    tool_id: string
    staged_upload_ids: number[]
  }>
}

export interface GenerateAgentTemplateDraftRequest {
  workspace_id: string
  template_id: string
  template_revision: string
  locale: string
  values: Record<string, unknown>
}

export interface AgentTemplateCatalogItemResponse {
  id: string
  revision: string
  category: "general" | "language_learning"
  title: Record<"en" | "zh-TW" | "ja", string>
  description: Record<"en" | "zh-TW" | "ja", string>
}

export interface GeneratedAgentTemplateDraftResponse {
  draft: {
    name: string
    description: string
    system_prompt: string
    unified_tools: UnifiedToolSchema[]
    llm_model: string
    suggested_questions: string[]
    pending_knowledge_bindings: PendingKnowledgeBindingSchema[]
    response_mode: AgentResponseMode
    human_handoff: HumanHandoffConfigSchema
  }
}

export interface StagedUploadedBlobSchema {
  id: number
  name: string
  content_type: string
  size_bytes: number
  expires_at: string
}

export interface PendingKnowledgeBindingSchema {
  tool_id: string
  files: StagedUploadedBlobSchema[]
}

export interface StagedUploadedBlobResponseSchema {
  files: StagedUploadedBlobSchema[]
  errors: Array<{
    name: string
    code:
      | "empty_file"
      | "file_too_large"
      | "invalid_file_metadata"
      | "unsupported_file_type"
  }>
}

export interface AgentTemplateDefinitionResponse extends AgentTemplateCatalogItemResponse {
  form: unknown[]
}

export interface OutputPresentationPolicySchema {
  enable_output_presentations: boolean
  allowed_presentation_kinds: string[]
  max_items_by_kind: Record<string, number>
  when_to_use_by_kind: Record<string, string>
}

const agentLLMModelSettingsToSchema = (
  settings?: AgentLLMModelSettings
): AgentLLMModelSettingsSchema => ({
  temperature: settings?.temperature,
  max_tokens: settings?.maxTokens,
  top_p: settings?.topP,
  thinking: settings?.thinking,
})

const agentLLMModelSettingsMapToSchema = (
  settingsMap?: AgentLLMModelSettingsMap,
  options?: { preserveEmpty?: boolean }
): Record<string, AgentLLMModelSettingsSchema> | undefined => {
  if (!settingsMap) return undefined
  const serialized = Object.entries(settingsMap).reduce<
    Record<string, AgentLLMModelSettingsSchema>
  >((acc, [modelId, settings]) => {
    const schema = agentLLMModelSettingsToSchema(settings)
    const hasValue = Object.values(schema).some((value) => value !== undefined)
    if (hasValue) {
      acc[modelId] = schema
    }
    return acc
  }, {})
  if (Object.keys(serialized).length > 0) {
    return serialized
  }
  return options?.preserveEmpty ? {} : undefined
}

const humanHandoffConfigToSchema = (
  config: HumanHandoffConfig = DEFAULT_HUMAN_HANDOFF_CONFIG
): HumanHandoffConfigSchema => {
  return {
    enabled: config.enabled,
    idle_timeout_minutes: config.idleTimeoutMinutes,
    handoff_instructions: config.handoffInstructions,
  }
}

const outputPresentationPolicyToSchema = (
  policy?: OutputPresentationPolicy
): OutputPresentationPolicySchema | undefined => {
  if (!policy) return undefined

  const allowedSet = new Set<string>(policy.allowedPresentationKinds)
  return {
    enable_output_presentations: policy.enableOutputPresentations,
    allowed_presentation_kinds: policy.allowedPresentationKinds,
    max_items_by_kind: Object.fromEntries(
      Object.entries(policy.maxItemsByKind || {})
        .filter(([kind]) => allowedSet.has(kind))
        .map(([kind, maxItems]) => [
          kind,
          clampOutputPresentationMaxItems(
            kind as OutputPresentationKind,
            maxItems
          ),
        ])
    ),
    when_to_use_by_kind: Object.fromEntries(
      Object.entries(policy.whenToUseByKind || {})
        .filter(
          ([kind, guidance]) => allowedSet.has(kind) && !!guidance?.trim()
        )
        .map(([kind, guidance]) => [kind, guidance.trim()])
    ),
  }
}

export const makeCreateAgentRequest = (
  name: string,
  description: string,
  workspaceId: string,
  systemPrompt: string,
  unifiedTools: UnifiedTool[],
  attachments: UploadingFile[] = [],
  llmModel?: string | null,
  llmModelSettings?: AgentLLMModelSettingsMap,
  suggestedQuestions: string[] = [],
  humanHandoff: HumanHandoffConfig = DEFAULT_HUMAN_HANDOFF_CONFIG,
  outputPresentationPolicy?: OutputPresentationPolicy,
  pendingKnowledgeBindings: PendingKnowledgeBindingSchema[] = [],
  responseMode: AgentResponseMode = DEFAULT_AGENT_RESPONSE_MODE,
  aiTakeoverGreeting: string = ""
): CreateAgentRequest => {
  const normalizedLlmModel = llmModel?.trim() ? llmModel : undefined
  return {
    name,
    description,
    workspace_id: workspaceId,
    system_prompt: systemPrompt,
    unified_tools: unifiedTools.map(unifiedToolToSchema),
    attachment_ids: attachments.map((att) => att.uuid as string),
    use_search: false,
    llm_model: normalizedLlmModel,
    llm_model_settings: agentLLMModelSettingsMapToSchema(llmModelSettings),
    suggested_questions: suggestedQuestions,
    response_mode: responseMode,
    human_handoff: humanHandoffConfigToSchema(humanHandoff),
    // Always send a string so clearing the field ("") disables the greeting.
    ai_takeover_greeting: normalizeAiTakeoverGreeting(aiTakeoverGreeting),
    output_presentation_policy: outputPresentationPolicyToSchema(
      outputPresentationPolicy
    ),
    pending_knowledge_bindings: pendingKnowledgeBindings.length
      ? pendingKnowledgeBindings.map((binding) => ({
          tool_id: binding.tool_id,
          staged_upload_ids: binding.files.map((file) => file.id),
        }))
      : undefined,
  }
}

export interface UnifiedToolSchema {
  id: string
  type: UnifiedToolType
  name?: string
  description?: string
  version?: number
  domain?: string
  invocation_instruction?: string
  draft_policy?: DraftPolicy | null
  agent_id?: string
  knowledge_node_ids?: string[]
  knowledge_node_id?: string
  image_model?: string
  custom_form_schema?: RequestFormSchema
  http_request?: HttpRequestToolSchema
  text2speech?: Text2SpeechToolSchema
}

export interface HttpRequestToolSchema {
  method: HttpRequestMethod
  url_template: string
  auth: {
    type: HttpRequestAuthType
    token?: string
    api_key?: string
    api_key_location?: HttpRequestAuthLocation
    api_key_param_name?: string
    username?: string
    password?: string
    header_name?: string
    header_value?: string
  }
  headers?: { key: string; value: string }[]
  query_params?: { key: string; value: string }[]
  body: {
    template?: unknown
    input_contracts?: Record<
      string,
      {
        type?: HttpRequestInputType
        format?: "text" | "json"
        json_type?: "any" | "object" | "array"
      }
    >
  }
}

export interface Text2SpeechToolSchema {
  provider: Text2SpeechProvider
  voice_id: string
  model_id: string
  language_code?: string | null
  output_format: string
}

export interface Text2SpeechLanguageOptionSchema {
  language_code: string
  name?: string | null
}

export interface Text2SpeechModelOptionSchema {
  model_id: string
  name: string
  languages: Text2SpeechLanguageOptionSchema[]
}

export interface Text2SpeechVoiceLanguageOptionSchema {
  language_code?: string | null
  model_id?: string | null
  accent?: string | null
  locale?: string | null
  preview_url?: string | null
}

export interface Text2SpeechVoiceOptionSchema {
  voice_id: string
  name: string
  preview_url?: string | null
  category?: string | null
  description?: string | null
  labels: Record<string, string>
  verified_languages: Text2SpeechVoiceLanguageOptionSchema[]
}

export interface UpdateAgentRequest {
  name: string
  description: string
  system_prompt: string
  unified_tools: UnifiedToolSchema[]
  attachment_ids: string[]
  use_search: boolean
  llm_model?: string | null
  llm_model_settings?: Record<string, AgentLLMModelSettingsSchema>
  version_note?: string
  suggested_questions?: string[]
  response_mode: AgentResponseMode
  human_handoff: HumanHandoffConfigSchema
  ai_takeover_greeting: string
  output_presentation_policy?: OutputPresentationPolicySchema
}

export const makeUpdateAgentRequest = (
  name: string,
  description: string,
  systemPrompt: string,
  unifiedTools: UnifiedTool[],
  attachments: UploadingFile[],
  llmModel?: string | null,
  llmModelSettings?: AgentLLMModelSettingsMap,
  versionNote?: string,
  suggestedQuestions: string[] = [],
  humanHandoff: HumanHandoffConfig = DEFAULT_HUMAN_HANDOFF_CONFIG,
  outputPresentationPolicy?: OutputPresentationPolicy,
  responseMode: AgentResponseMode = DEFAULT_AGENT_RESPONSE_MODE,
  aiTakeoverGreeting: string = ""
): UpdateAgentRequest => {
  const normalizedLlmModel =
    llmModel === undefined ? undefined : llmModel?.trim() ? llmModel : null
  return {
    name,
    description,
    system_prompt: systemPrompt,
    unified_tools: unifiedTools.map(unifiedToolToSchema),
    attachment_ids: attachments.map((att) => att.uuid as string),
    use_search: false, // no use for now
    llm_model: normalizedLlmModel,
    llm_model_settings: agentLLMModelSettingsMapToSchema(llmModelSettings, {
      preserveEmpty: true,
    }),
    version_note: versionNote ?? "",
    suggested_questions: suggestedQuestions,
    response_mode: responseMode,
    human_handoff: humanHandoffConfigToSchema(humanHandoff),
    // Always send a string so clearing the field ("") disables the greeting.
    ai_takeover_greeting: normalizeAiTakeoverGreeting(aiTakeoverGreeting),
    output_presentation_policy: outputPresentationPolicyToSchema(
      outputPresentationPolicy
    ),
  }
}
export interface AgentResponse extends AgentSchema {}

export const transformText2SpeechLanguageOption = (
  response: Text2SpeechLanguageOptionSchema
): Text2SpeechLanguageOption => ({
  languageCode: response.language_code,
  name: response.name || undefined,
})

export const transformText2SpeechModelOption = (
  response: Text2SpeechModelOptionSchema
): Text2SpeechModelOption => ({
  modelId: response.model_id,
  name: response.name,
  languages: (response.languages || []).map(transformText2SpeechLanguageOption),
})

export const transformText2SpeechVoiceLanguageOption = (
  response: Text2SpeechVoiceLanguageOptionSchema
): Text2SpeechVoiceLanguageOption => ({
  languageCode: response.language_code || undefined,
  modelId: response.model_id || undefined,
  accent: response.accent || undefined,
  locale: response.locale || undefined,
  previewUrl: response.preview_url || undefined,
})

export const transformText2SpeechVoiceOption = (
  response: Text2SpeechVoiceOptionSchema
): Text2SpeechVoiceOption => ({
  voiceId: response.voice_id,
  name: response.name,
  previewUrl: response.preview_url || undefined,
  category: response.category || undefined,
  description: response.description || undefined,
  labels: response.labels || {},
  verifiedLanguages: (response.verified_languages || []).map(
    transformText2SpeechVoiceLanguageOption
  ),
})

export { transToAgent as agentResponseToAgent } from "./types"

export const useCreateAgentApi = () => {
  return useCodeerApi<Response<AgentSchema>, CreateAgentRequest>(
    `/agents`,
    FetchMethod.Post
  )
}

export const useGenerateAgentTemplateDraftApi = () => {
  return useCodeerApi<
    Response<GeneratedAgentTemplateDraftResponse>,
    GenerateAgentTemplateDraftRequest
  >(`/agents/template-draft/generate`, FetchMethod.Post)
}

export const useUploadAgentTemplateKnowledgeApi = () => {
  return useCodeerApi<Response<StagedUploadedBlobResponseSchema>, FormData>(
    `/agents/template-knowledge-uploads`,
    FetchMethod.Post
  )
}

export const useDeleteAgentTemplateKnowledgeUploadApi = () => {
  return useCodeerApi<Response<never>>(
    `/agents/template-knowledge-uploads/__placeholder__`,
    FetchMethod.Delete,
    true,
    (params) =>
      `/agents/template-knowledge-uploads/${encodeURIComponent(String(params.uploadId))}`
  )
}

export const useListAgentTemplatesApi = () => {
  return useCodeerApi<Response<AgentTemplateCatalogItemResponse[]>>(
    "/agents/templates",
    FetchMethod.Get
  )
}

export const useGetAgentTemplateDefinitionApi = () => {
  return useCodeerApi<Response<AgentTemplateDefinitionResponse>>(
    "/agents/templates/__placeholder__",
    FetchMethod.Get,
    true,
    (params) =>
      `/agents/templates/${encodeURIComponent(String(params.templateId))}`
  )
}

export const useGetAgentApi = (agentId: string) => {
  return useCodeerApi<Response<AgentResponse>>(
    `/agents/${agentId}`,
    FetchMethod.Get
  )
}

export const useGetAgentsApi = () => {
  return useCodeerApi<Response<AgentResponse[]>>("/agents/all", FetchMethod.Get)
}

export const useGetPublishedAgentsApi = () => {
  return useCodeerApi<Response<AgentResponse[]>>("/agents", FetchMethod.Get)
}

export const useUpdateAgentApi = (id: string) => {
  return useCodeerApi<Response<AgentResponse>, UpdateAgentRequest>(
    `/agents/${id}`,
    FetchMethod.Put
  )
}

export const useDeleteAgentApi = (id: string) => {
  return useCodeerApi<Response<unknown>>(`/agents/${id}`, FetchMethod.Delete)
}

export const useGetText2SpeechModelsApi = () => {
  return useCodeerApi<Response<Text2SpeechModelOptionSchema[]>>(
    "/agents/tool-options/text2speech/models",
    FetchMethod.Get
  )
}

export const useGetText2SpeechVoicesApi = () => {
  return useCodeerApi<Response<Text2SpeechVoiceOptionSchema[]>>(
    "/agents/tool-options/text2speech/voices",
    FetchMethod.Get
  )
}

// Agent History APIs
export interface PublishHistoryRequest {
  history_id: string
  unpublish?: boolean
}

export const usePublishHistoryApi = (agentId: string) => {
  return useCodeerApi<Response<AgentResponse>, PublishHistoryRequest>(
    `/agents/${agentId}/publish-history`,
    FetchMethod.Post
  )
}

export const useGetAgentHistoriesApi = (agentId: string) => {
  return useCodeerApi<Response<AgentHistorySummaryResponse[]>>(
    `/agents/${agentId}/histories`,
    FetchMethod.Get
  )
}

export const useGetAgentHistoryDetailApi = (
  agentId: string,
  historyId: string
) => {
  return useCodeerApi<Response<AgentHistoryResponse>>(
    `/agents/${agentId}/histories/${historyId}`,
    FetchMethod.Get
  )
}

// Agent History Types
export interface AgentHistoryResponse {
  id: string
  agent_id: string
  version_number: number
  name: string
  description?: string
  version_note?: string
  system_prompt: string
  meta: Record<string, unknown>
  unified_tools: UnifiedToolSchema[]
  suggested_questions?: string[]
  response_mode?: AgentResponseMode
  human_handoff?: HumanHandoffConfigSchema
  ai_takeover_greeting?: string | null
  use_search: boolean
  llm_model?: string
  llm_model_settings?: Record<string, AgentLLMModelSettingsSchema>
  attachments?: {
    id: string
    original_name: string
    content_type: string
    size: number
    source_url: string | null
    provider: string | null
    scope: string
    created_at: string
  }[]
  created_by?: {
    id: string
    name: string
    email: string
  }
  created_at: string
  status: "draft" | "published" | "archived"
  was_published: boolean
  published_at?: string
  supports_image_region_editing?: boolean
}

export interface AgentHistorySummaryResponse {
  id: string
  version_number: number
  name: string
  version_note?: string
  created_at: string
  created_by?: {
    id: string
    name: string
    email: string
  }
  was_published: boolean
  status: "draft" | "published" | "archived"
  supports_image_region_editing?: boolean
}

export const agentHistoryResponseToAgentHistory = (
  resp: AgentHistoryResponse
): AgentHistory => {
  return {
    id: resp.id,
    agentId: resp.agent_id,
    versionNumber: resp.version_number,
    name: resp.name,
    description: resp.description,
    versionNote: resp.version_note,
    systemPrompt: resp.system_prompt,
    meta: resp.meta ?? {},
    unifiedTools: (resp.unified_tools ?? []).map(transToUnifiedTool),
    suggestedQuestions: resp.suggested_questions,
    responseMode: resp.response_mode ?? DEFAULT_AGENT_RESPONSE_MODE,
    humanHandoff: transToHumanHandoffConfig(resp.human_handoff),
    aiTakeoverGreeting: transToAiTakeoverGreeting(resp),
    useSearch: resp.use_search ?? false,
    llmModel: resp.llm_model,
    llmModelSettings: transToAgentLLMModelSettingsMap(resp.llm_model_settings),
    outputPresentationPolicy: transToOutputPresentationPolicy(
      resp.meta?.output_presentation_policy as
        | AgentResponseOutputPresentationPolicySchema
        | undefined
    ),
    attachments: apiAttachmentsToUploadingFiles(resp.attachments ?? []),
    createdBy: resp.created_by,
    createdAt: resp.created_at,
    status: resp.status,
    wasPublished: resp.was_published,
    publishedAt: resp.published_at,
    supportsImageRegionEditing: resp.supports_image_region_editing ?? false,
  }
}

export const agentHistorySummaryResponseToAgentHistory = (
  resp: AgentHistorySummaryResponse
): AgentHistory => {
  return {
    id: resp.id,
    agentId: "", // Not available in summary
    versionNumber: resp.version_number,
    name: resp.name,
    description: undefined, // Not available in summary
    versionNote: resp.version_note,
    systemPrompt: "", // Not available in summary
    meta: {}, // Not available in summary
    unifiedTools: [], // Not available in summary
    responseMode: DEFAULT_AGENT_RESPONSE_MODE,
    humanHandoff: { ...DEFAULT_HUMAN_HANDOFF_CONFIG },
    aiTakeoverGreeting: "", // Not available in summary
    useSearch: false, // Not available in summary
    llmModel: undefined, // Not available in summary
    createdBy: resp.created_by,
    createdAt: resp.created_at,
    status: resp.status,
    wasPublished: resp.was_published,
    publishedAt: undefined, // Not available in summary
    supportsImageRegionEditing: resp.supports_image_region_editing ?? false,
  }
}

// Agent Impact Check Types and API
export interface ImpactedAgent {
  agent_id: string
  agent_name: string
  versions: string // Formatted version string like "v1, v2, v3 ... v50"
}

export interface AgentImpactCheckResponse {
  impacted_agents: ImpactedAgent[]
  total_count: number
}

export const useGetAgentImpactApi = (agentId: string) => {
  return useCodeerApi<Response<AgentImpactCheckResponse>>(
    `/agents/${agentId}/impact`,
    FetchMethod.Get
  )
}
